<template>
  <div class="file-preview-wrapper">
    <!-- Loading -->
    <div v-if="loading" class="file-preview-state">
      <i class="el-icon-loading" style="font-size:36px"></i>
      <span style="margin-top:10px">Đang tải file...</span>
    </div>

    <!-- Lỗi -->
    <div v-else-if="error" class="file-preview-state file-preview-error">
      <i class="el-icon-warning-outline" style="font-size:36px"></i>
      <p>{{ error }}</p>
      <el-button size="small" type="primary" @click="loadFile">Thử lại</el-button>
      <el-button size="small" @click="$emit('download')">Tải file về</el-button>
    </div>

    <!-- PDF: nhiều canvas, mỗi canvas = 1 trang -->
    <div v-else-if="fileType === 'pdf'" class="pdf-pages-container">
      <canvas
        v-for="p in totalPages"
        :key="p"
        :id="'pdf-canvas-' + _uid + '-' + p"
        class="pdf-page-canvas"
      ></canvas>
    </div>

    <!-- DOCX: HTML được convert bởi mammoth -->
    <div
      v-else-if="fileType === 'docx'"
      class="docx-container"
      v-html="docxHtml"
    ></div>

    <!-- Excel: bảng HTML -->
    <div v-else-if="fileType === 'excel'" class="excel-container">
      <table class="excel-table">
        <tbody>
          <tr v-for="(row, ri) in excelRows" :key="ri">
            <td v-for="(cell, ci) in row" :key="ci">{{ cell }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Định dạng không hỗ trợ -->
    <div v-else-if="fileType === 'unsupported'" class="file-preview-state">
      <i class="el-icon-document" style="font-size:36px"></i>
      <p>Không hỗ trợ xem trước định dạng này</p>
      <el-button size="small" type="primary" @click="$emit('download')">Tải file về</el-button>
    </div>
  </div>
</template>

<script>
import * as PDFJS from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';
import mammoth from 'mammoth';
import readXlsxFile from 'read-excel-file';

export default {
  name: 'FilePreview',
  props: {
    fileUrl: {
      type: String,
      default: '',
    },
    // Phần mở rộng không có dấu chấm: 'pdf', 'docx', 'xlsx', ...
    extension: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      loading: false,
      error: null,
      fileType: null,
      // PDF
      totalPages: 0,
      // DOCX
      docxHtml: '',
      // Excel
      excelRows: [],
    };
  },
  watch: {
    fileUrl(val) {
      if (val) this.loadFile();
    },
    extension() {
      if (this.fileUrl) this.loadFile();
    },
  },
  methods: {
    detectType(ext) {
      const e = (ext || '').toLowerCase().replace('.', '');
      if (e === 'pdf') return 'pdf';
      if (['docx', 'doc'].includes(e)) return 'docx';
      if (['xlsx', 'xls'].includes(e)) return 'excel';
      return 'unsupported';
    },

    getToken() {
      const rootApiUrl = window.MyConfig && window.MyConfig.hostApi;
      if (!rootApiUrl) return null;
      return localStorage.getItem('mushroom.tokens[' + rootApiUrl + ']');
    },

    buildFetchUrl(rawUrl) {
      // Blob URL (file vừa upload local): browser đọc trực tiếp, không cần proxy
      if (rawUrl && rawUrl.startsWith('blob:')) return rawUrl;

      // Trong development, chuyển URL tuyệt đối sang /file-proxy/...
      // để đi qua devServer proxy — tránh CORS khi FE và API khác port.
      // Trong production FE và API cùng domain nên dùng URL gốc.
      if (process.env.NODE_ENV === 'development') {
        try {
          const parsed = new URL(rawUrl);
          // /api/pmcc/v1/file/... → /file-proxy/file/...
          const relativePath = parsed.pathname.replace('/api/pmcc/v1', '') + parsed.search;
          return '/file-proxy' + relativePath;
        } catch (e) { /* URL không hợp lệ, dùng nguyên */ }
      }
      return rawUrl;
    },

    async fetchArrayBuffer() {
      const url = this.buildFetchUrl(this.fileUrl);
      const isBlobUrl = this.fileUrl && this.fileUrl.startsWith('blob:');
      const headers = {};
      // Blob URL không cần auth token (là file local trong memory)
      if (!isBlobUrl) {
        const token = this.getToken();
        if (token) headers['Token'] = token;
      }
      const response = await fetch(url, {
        method: 'GET',
        headers,
        credentials: isBlobUrl ? 'omit' : 'include',
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.arrayBuffer();
    },

    async loadFile() {
      if (!this.fileUrl) return;
      this.fileType = this.detectType(this.extension);
      this.loading = true;
      this.error = null;
      this.totalPages = 0;
      this.docxHtml = '';
      this.excelRows = [];

      try {
        const arrayBuffer = await this.fetchArrayBuffer();

        if (this.fileType === 'pdf') {
          await this.renderPdf(arrayBuffer);
        } else if (this.fileType === 'docx') {
          await this.renderDocx(arrayBuffer);
          this.loading = false;
        } else if (this.fileType === 'excel') {
          await this.renderExcel(arrayBuffer);
          this.loading = false;
        }
      } catch (err) {
        console.error('[FilePreview] Lỗi:', err);
        this.error = 'Không thể tải file. Vui lòng thử lại.';
        this.loading = false;
      }
    },

    async renderPdf(arrayBuffer) {
      PDFJS.GlobalWorkerOptions.workerSrc = pdfjsWorker;
      const pdfDoc = await PDFJS.getDocument({ data: arrayBuffer }).promise;
      this.totalPages = pdfDoc.numPages;

      // Tắt loading TRƯỚC để v-else-if="fileType === 'pdf'" hiện ra
      // và các <canvas> được mount vào DOM
      this.loading = false;
      await this.$nextTick();

      for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum++) {
        const page = await pdfDoc.getPage(pageNum);
        const viewport = page.getViewport({ scale: 1.4 });
        const canvas = document.getElementById('pdf-canvas-' + this._uid + '-' + pageNum);
        if (!canvas) continue;
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({
          canvasContext: canvas.getContext('2d'),
          viewport,
        }).promise;
      }
    },

    async renderDocx(arrayBuffer) {
      const result = await mammoth.convertToHtml({ arrayBuffer });
      this.docxHtml = result.value;
    },

    async renderExcel(arrayBuffer) {
      const blob = new Blob([arrayBuffer]);
      const rows = await readXlsxFile(blob);
      this.excelRows = rows;
    },
  },
  created() {
    if (this.fileUrl) this.loadFile();
  },
};
</script>

<style scoped>
.file-preview-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  background: #f5f5f5;
}

.file-preview-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 300px;
  gap: 12px;
  color: #909399;
  font-size: 14px;
}

.file-preview-error {
  color: #f56c6c;
}

/* PDF */
.pdf-pages-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px;
  gap: 12px;
}

.pdf-page-canvas {
  max-width: 100%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  background: #fff;
}

/* DOCX */
.docx-container {
  padding: 32px 48px;
  background: #fff;
  min-height: 100%;
  font-size: 14px;
  line-height: 1.8;
  color: #333;
  overflow-y: auto;
}

/* Excel */
.excel-container {
  overflow: auto;
  padding: 16px;
  background: #fff;
}

.excel-table {
  border-collapse: collapse;
  width: 100%;
  font-size: 13px;
}

.excel-table td {
  border: 1px solid #dcdfe6;
  padding: 6px 10px;
  white-space: nowrap;
}

.excel-table tr:first-child td {
  background: #f0f2f5;
  font-weight: 600;
}
</style>
